// Recruit Core Factors — off-field + on-field traits + position skills, 1–5 scale (scouting report).
import { isSkillKey, skillKeys, skillLabel } from "./positionSkills";

export const OFF_FIELD = [
  ["character", "Character"],
  ["work_ethic", "Work ethic"],
  ["coachability", "Coachability"],
  ["competitiveness", "Competitiveness"],
  ["commitment", "Commitment / reliability"],
  ["discipline", "Discipline"],
  ["durability", "Durability / injury history"],
];
export const ON_FIELD = [
  ["athletic_ability", "Athletic ability"],
  ["football_iq", "Football IQ (instincts)"],
  ["physicality", "Physicality / toughness"],
  ["production", "Production"],
  ["playmaking", "Playmaking"],
  ["speed_explosiveness", "Speed & explosiveness"],
  ["effort_motor", "Effort / motor"],
  ["technique", "Technique / fundamentals"],
  ["versatility", "Versatility"],
];
export const ALL_CORE = [...OFF_FIELD, ...ON_FIELD];
export const CORE_SCALE = ["Poor", "Below average", "Average", "Good", "Elite"];
// Recruits: every evaluator card grades core factors. Players: the two position-skill graders.
export const CORE_SLOTS = [["position_coach", "Position Coach", "PC"], ["coordinator", "Coordinator", "CO"], ["gm", "GM", "GM"], ["chief_of_staff", "Chief of Staff", "COS"], ["head_coach", "Head Coach", "HC"]];
export const SKILL_SLOTS = CORE_SLOTS;
// Player Position Skills: GM / Chief of Staff / Head Coach tabs belong to that role only; PC + Coordinator tabs stay open to every grader.
const SKILL_TAB_OWNER = { gm: "gm", chief_of_staff: "chief_of_staff", head_coach: "head_coach" };
export const skillTabsFor = (role) => SKILL_SLOTS.map(([k]) => k).filter((k) => !SKILL_TAB_OWNER[k] || role === "super_admin" || SKILL_TAB_OWNER[k] === role);
export const slotMeta = (k) => CORE_SLOTS.find(([s]) => s === k) || [k, k, k];
export const SPLIT_GAP = 2;

const avgKeys = (cf, keys) => {
  const v = keys.map((k) => cf?.[k]).filter((x) => typeof x === "number" && x >= 1 && x <= 5);
  return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null;
};
const keysOf = (list) => list.map(([k]) => k);
export const coreLabel = (k) => (isSkillKey(k) ? skillLabel(k) : ALL_CORE.find(([x]) => x === k)?.[1] || k);
// Every factor key that counts for this recruit (universal + his position's skills).
export const coreKeys = (cf, position) => [...keysOf(ALL_CORE), ...skillKeys(cf, position)];

// Simple averages of whatever is rated; position skills count toward On-field.
export function coreSummary(cf, position) {
  const ps = skillKeys(cf, position);
  const on = [...keysOf(ON_FIELD), ...ps];
  const all = [...keysOf(OFF_FIELD), ...on];
  return { off: avgKeys(cf, keysOf(OFF_FIELD)), on: avgKeys(cf, on), pos: avgKeys(cf, ps), overall: avgKeys(cf, all), rated: all.filter((k) => cf?.[k]).length };
}
const slotKeys = (slots) => [...new Set([...keysOf(ALL_CORE), ...Object.values(slots || {}).flatMap((s) => Object.keys(s?.factors || {}).filter(isSkillKey))])];
// Every rating given for one factor: [[slot, value], …] in card order.
export const ratingsFor = (slots, k) => CORE_SLOTS.map(([s]) => [s, slots?.[s]?.factors?.[k]]).filter(([, v]) => v);

// Coach slots; ratings entered before two-coach slots count as the Position Coach's.
export function slotsOf(r) {
  const cr = r?.core_ratings || {};
  if (!Object.keys(cr).length && Object.keys(r?.core_factors || {}).length) return { position_coach: { factors: r.core_factors, legacy: true } };
  return cr;
}

// Consensus = average of every evaluator who rated that factor.
export function consensusOf(slots) {
  const out = {};
  slotKeys(slots).forEach((k) => {
    const v = ratingsFor(slots, k).map(([, x]) => x);
    if (v.length) out[k] = v.reduce((a, b) => a + b, 0) / v.length;
  });
  return out;
}

// Split = any two evaluators 2+ points apart on a factor.
export function splitsOf(slots) {
  return slotKeys(slots).filter((k) => {
    const v = ratingsFor(slots, k).map(([, x]) => x);
    return v.length > 1 && Math.max(...v) - Math.min(...v) >= SPLIT_GAP;
  });
}

export const coreColor = (v) => (v == null ? "#71717a" : v >= 4.5 ? "#16a34a" : v >= 3.5 ? "#84cc16" : v >= 2.5 ? "#eab308" : v >= 1.5 ? "#f97316" : "#ef4444");
export const fmtCore = (v) => (v == null ? "—" : (Math.round(v * 10) / 10).toFixed(1));
export const fmtFactor = (v) => (v == null ? "—" : Number.isInteger(v) ? String(v) : (Math.round(v * 10) / 10).toFixed(1));
